/**
 * <FuelCardForm /> — shared input panel for both:
 *   - Customer Profile → Cards table → Add/Edit
 *   - Fuel Cards page → Add modal, Edit modal
 *
 * Single source of truth for the fuel-card data shape. The label "Driver ID"
 * is rendered as "Driver Name" — the backend field stays `driver_id` per the
 * May 2026 product call so existing API contracts + indexes don't churn.
 *
 * Props:
 *   value         — controlled form state { network, card_number, status,
 *                   customer_id, truck_id, driver_id, unit, notes }
 *   onChange      — receives partial-update object: onChange({ key: val })
 *   isEdit        — when true the Network <select> is disabled (can't move
 *                   a card between networks once it has matched transactions).
 *   customers     — optional list of {customer_id, company_name}; when
 *                   provided, renders a Customer <select>. Omit on the
 *                   Customer Profile page (the customer is implicit).
 *   testIdPrefix  — string prefix for `data-testid` so callers can have
 *                   stable test selectors (defaults to "fuel-card-form").
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

// Phase FC-B.2R (Feb 2026) · Country is NOT part of Petro-Pass card
// identity. A single physical PP card can fuel in both Canada and the
// USA, so the Add/Edit Fuel Card form intentionally has no Country
// selector. Backend FC-A.2 strips any `country` field before storage.

// Phase FC-B · map FC-A structured error codes to plain-language operator
// messages. Callers (Cards.jsx / CustomerDetail.jsx) may import this
// helper to surface backend rejections in their existing error UI.
export function ppCardErrorMessage(detail) {
  const code = detail?.code || detail?.detail?.code;
  switch (code) {
    case "PP_CARD_NUMBER_REQUIRED":
      return "Enter the complete Petro-Pass card number.";
    case "PP_CARD_NUMBER_TOO_SHORT":
      return "The Petro-Pass card number appears incomplete.";
    case "PP_CARD_DUPLICATE":
      return "This Petro-Pass card already exists.";
    default:
      return detail?.message || detail?.detail?.message || null;
  }
}

export default function FuelCardForm({
  value,
  onChange,
  isEdit = false,
  customers = null,
  testIdPrefix = "fuel-card-form",
  /** When supplied, the form skips the row currently being edited so
   * its own card_number doesn't false-positive against itself in the
   * collision check. */
  excludeCardId = null,
}) {
  const set = (patch) => onChange(patch);
  const tid = (k) => `${testIdPrefix}-${k}`;

  // ---- Soft uniqueness check (op req 2026-05-22) ----
  // Debounced collision lookup against GET /cards/check-collision.
  // Renders a yellow warning under the card-number input listing
  // every other card on the same network already using this number
  // (full / lstripped / last-5 / last-4 variant match). Never blocks
  // the save — admins decide whether to re-attribute or keep both.
  const [collisions, setCollisions] = useState([]);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    const cn = (value.card_number || "").trim();
    const nw = value.network || "";
    if (!cn || cn.length < 3 || !nw) {
      setCollisions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const params = { network: nw, card_number: cn };
        if (excludeCardId) params.exclude_card_id = excludeCardId;
        const { data } = await api.get("/cards/check-collision", { params });
        setCollisions(data?.collisions || []);
      } catch {
        setCollisions([]);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value.card_number, value.network, excludeCardId]);

  return (
    <div className="gf-card-body space-y-3" data-testid={testIdPrefix}>
      <div>
        <div className="overline mb-1">Network</div>
        <select
          className="gf-input"
          value={value.network || "esso"}
          onChange={(e) => {
            const newNet = e.target.value;
            const patch = { network: newNet };
            // Phase FC-B.2R · country is never part of the card form.
            // Clear any stale country left over from earlier drafts /
            // legacy records so we never submit it to the backend.
            if (value.country) {
              patch.country = "";
            }
            set(patch);
          }}
          disabled={isEdit}
          data-testid={tid("network")}
        >
          <option value="esso">Esso</option>
          <option value="pipeline">Pipeline</option>
          <option value="petro_pass">Petro-Pass</option>
        </select>
      </div>

      <div>
        <div className="overline mb-1">Card number</div>
        <input
          className="gf-input font-mono"
          value={value.card_number || ""}
          onChange={(e) => set({ card_number: e.target.value })}
          placeholder={
            value.network === "esso"
              ? "e.g. 7064 1234 5678 9012"
              : value.network === "petro_pass"
              ? "e.g. 1234-5678-9012-3456"
              : "e.g. 1234"
          }
          data-testid={tid("card-number")}
          autoFocus={!isEdit}
        />
        <div className="text-[11px] text-zinc-500 mt-1">
          {value.network === "esso"
            ? "Esso: full 16-digit card number. Last 5 digits are matched to transactions."
            : value.network === "petro_pass"
            ? "Enter the complete Petro-Pass card number. Only the last 4 digits will be shown in normal card displays."
            : value.network === "pipeline"
            ? "Pipeline: 4-digit card number from your fleet listing."
            : ""}
        </div>
        {checking && (
          <div className="text-[11px] text-zinc-400 mt-1" data-testid={tid("collision-checking")}>
            Checking uniqueness…
          </div>
        )}
        {!checking && collisions.length > 0 && (() => {
          // Status-mismatch escalation (op req 2026-05-22): if BOTH the
          // new card and any colliding card are "active", upgrade the
          // pale-amber warning to an orange "BOTH ACTIVE — verify"
          // banner. Helps catch the rare case where a card moves
          // between sister-companies but the old assignment was never
          // deactivated.
          const newStatus = (value.status || "active").toLowerCase();
          const bothActiveCollisions = collisions.filter(
            (c) => (c.status || "active").toLowerCase() === "active",
          );
          const escalate = newStatus === "active" && bothActiveCollisions.length > 0;
          const palette = escalate
            ? {
              wrap: "border-orange-400 bg-orange-50 text-orange-900",
              head: "BOTH ACTIVE — verify which fleet currently owns this card",
              foot: "Saving will create a dual-active registration. Deactivate the existing card on the other customer first if it's no longer in use.",
              testid: tid("collision-warning-both-active"),
            }
            : {
              wrap: "border-amber-300 bg-amber-50 text-amber-900",
              head: `⚠ This card number is already registered (${collisions.length} ${collisions.length === 1 ? "match" : "matches"}).`,
              foot: "Saving will create a second registration. To re-attribute, deactivate the existing card first.",
              testid: tid("collision-warning"),
            };
          return (
            <div
              data-testid={palette.testid}
              className={`mt-2 rounded border ${palette.wrap} p-2 text-xs`}
            >
              <div className="font-medium mb-1">{palette.head}</div>
              <ul className="list-disc ml-4 space-y-0.5">
                {collisions.slice(0, 5).map((c) => {
                  const isActive = (c.status || "active").toLowerCase() === "active";
                  return (
                    <li key={c.card_id} data-testid={`${tid("collision-row")}-${c.card_id}`}>
                      <span className="font-mono">{c.card_number}</span>
                      {" · "}
                      <span className="font-medium">{c.customer_name}</span>
                      <span
                        className={`ml-1 px-1 rounded text-[10px] ${
                          isActive ? "bg-orange-200 text-orange-900" : "bg-zinc-200 text-zinc-700"
                        }`}
                      >
                        {c.status || "active"}
                      </span>
                      {c.unit && <span className="ml-1 text-zinc-500">unit {c.unit}</span>}
                    </li>
                  );
                })}
                {collisions.length > 5 && (
                  <li className="text-zinc-600">…and {collisions.length - 5} more.</li>
                )}
              </ul>
              <div className="mt-1 text-[11px] opacity-80">{palette.foot}</div>
            </div>
          );
        })()}
      </div>

      {customers !== null && (
        <div>
          <div className="overline mb-1">Customer</div>
          <select
            className="gf-input"
            value={value.customer_id || ""}
            onChange={(e) => set({ customer_id: e.target.value })}
            data-testid={tid("customer")}
          >
            <option value="">— unassigned —</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <div className="overline mb-1">Status</div>
        <select
          className="gf-input"
          value={value.status || "active"}
          onChange={(e) => set({ status: e.target.value })}
          data-testid={tid("status")}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="lost">Lost / stolen</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="overline mb-1">Unit #</div>
          <input
            className="gf-input"
            value={value.unit || ""}
            onChange={(e) => set({ unit: e.target.value })}
            placeholder="e.g. 102, JS1014"
            data-testid={tid("unit")}
          />
        </div>
        <div>
          <div className="overline mb-1">Truck ID</div>
          <input
            className="gf-input"
            value={value.truck_id || ""}
            onChange={(e) => set({ truck_id: e.target.value })}
            data-testid={tid("truck")}
          />
        </div>
      </div>

      <div>
        {/* Backend field stays `driver_id` — display label is "Driver Name"
            per May 2026 spec. */}
        <div className="overline mb-1">Driver Name</div>
        <input
          className="gf-input"
          value={value.driver_id || ""}
          onChange={(e) => set({ driver_id: e.target.value })}
          placeholder="e.g. J. Smith"
          data-testid={tid("driver")}
        />
      </div>

      <div>
        {/* Phone # is OPTIONAL — no asterisk, no client-side required
            validation. Reasonable Canadian phone shape is checked
            lazily (informational only) when the user has entered
            something; blank saves cleanly. */}
        <div className="overline mb-1">Phone #</div>
        <input
          type="tel"
          className="gf-input"
          value={value.phone || ""}
          onChange={(e) => set({ phone: e.target.value })}
          placeholder="e.g. +1 416-555-0123 (optional)"
          autoComplete="off"
          data-testid={tid("phone")}
        />
        {value.phone && !/^\+?[0-9().\-\s]{7,20}$/.test(value.phone.trim()) && (
          <div
            className="text-[11px] text-amber-700 mt-1"
            data-testid={tid("phone-hint")}
          >
            Enter a valid North American phone number (digits, spaces, +,
            -, or parentheses).
          </div>
        )}
      </div>

      <div>
        <div className="overline mb-1">Notes</div>
        <input
          className="gf-input"
          value={value.notes || ""}
          onChange={(e) => set({ notes: e.target.value })}
          placeholder="Any additional notes"
          data-testid={tid("notes")}
        />
      </div>
    </div>
  );
}
